/**
 * Helpers for connecting a form to an event (#919). The Settings section, the event picker and the public form's
 * closed screens share them.
 *
 * Dates here are for RESPONDENTS and read the offset on the event's own date, so a "Thu, Nov 6, 2026 · 9:00 AM (GMT+8)
 * Manila (PHT)" line is right in any viewer's browser zone. In-app screens (the picker rows, the builder card) use the
 * Events workspace format instead (`lib/eventWorkspace.formatEventWhen`), so the builder reads like the Events list.
 */
import { formatEventWhen } from './eventWorkspace';
import { flattenInputFields, getAllFieldsFromSteps } from './formUtils';
import { browserTimeZone, formatInZone, isValidTimeZone, naiveInZone, zoneLabelAt } from './timezone';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(\d{2}):(\d{2})/;

/**
 * "Thu, Nov 6, 2026 · 9:00 AM (GMT+8) Manila (PHT)", for an event summary `{date, start_time, timezone}`.
 * With no start time the date stands alone ("Thu, Nov 6, 2026"); with no readable date it is "Date to be confirmed".
 * The wall clock is read IN the event's zone (a missing or unknown zone reads as UTC, like the server), never the
 * browser's.
 */
export function formatRegistrationWhen(event) {
    const date = typeof event?.date === 'string' ? event.date.trim().slice(0, 10) : '';
    if (!DATE_ONLY_RE.test(date)) return 'Date to be confirmed';
    const zone = isValidTimeZone(event?.timezone) ? event.timezone.trim() : 'UTC';
    const time = TIME_RE.exec(typeof event?.start_time === 'string' ? event.start_time.trim() : '');
    if (!time) {
        const midday = naiveInZone(`${date}T12:00`, zone);
        return formatInZone(midday, { zone, formatStr: 'EEE, MMM d, yyyy' }) || 'Date to be confirmed';
    }
    const instant = naiveInZone(`${date}T${time[1]}:${time[2]}`, zone);
    const when = formatInZone(instant, { zone, formatStr: 'EEE, MMM d, yyyy · h:mm a' });
    if (!when) return 'Date to be confirmed';
    const label = zoneLabelAt(zone, instant);
    return label ? `${when} ${label}` : when;
}

/**
 * "Thu 6 Nov · 9:00 am (GMT+8)": an event's start the way the Events list prints it (E56). The picker's options and the
 * builder card use this one, so the builder reads like the Events workspace. Respondent-facing screens use
 * `formatRegistrationWhen` instead.
 */
export function inAppEventWhen(event) {
    return formatEventWhen({
        local_date: typeof event?.date === 'string' ? event.date.trim().slice(0, 10) : '',
        start_time: event?.start_time,
        timezone: event?.timezone,
    });
}

// ─── Calendar helpers (pure string arithmetic on "YYYY-MM-DD", never the browser's zone) ────────────────────────

/** The "YYYY-MM-DD" part of a date or date-time string, or '' when it is not one. */
function dayOf(value) {
    const day = typeof value === 'string' ? value.trim().slice(0, 10) : '';
    return DATE_ONLY_RE.test(day) ? day : '';
}

function addDays(day, delta) {
    const [year, month, date] = day.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, date + delta)).toISOString().slice(0, 10);
}

/** "09:30" or "09:30:00" -> {minutes, text: "09:30"}, or null when it is not a clock time. */
function clockOf(value) {
    const match = TIME_RE.exec(typeof value === 'string' ? value.trim() : '');
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return null;
    return { minutes: hours * 60 + minutes, text: `${match[1]}:${match[2]}` };
}

/**
 * Yesterday's calendar day ("2026-11-05") in `zone` (the browser's by default).
 *
 * The event picker starts its `date_from` a day back, so an event that is still running in a zone behind the
 * viewer's stays listed (E18).
 */
export function yesterdayInZone(now = new Date(), zone = browserTimeZone()) {
    const today = formatInZone(now, { zone: isValidTimeZone(zone) ? zone : 'UTC', formatStr: 'yyyy-MM-dd' });
    return today ? addDays(today, -1) : '';
}

// ─── The registration window (E12) ───────────────────────────────────────────────────────────────────────────

/**
 * When registration on `event` ends, as epoch ms, or null for an undated event (which is never "over").
 *
 * The same rule as the server's `form_event_registration.registration_window` (E12), in the event's own zone
 * (a missing or unknown zone reads as UTC):
 *   - the last day is the calendar day of `end_date` when present, else of `date` (`start_date` on legacy rows);
 *   - normally the end is `end_time` on the last day;
 *   - when `end_time <= start_time` the event crosses midnight and ends the next day;
 *   - with no `end_time`, it is `start_time + duration_minutes`;
 *   - with neither, or a time that doesn't parse, it is the end of the last day.
 */
export function registrationWindowEnd(event) {
    const firstDay = dayOf(event?.date) || dayOf(event?.start_date);
    if (!firstDay) return null;
    const lastDay = dayOf(event?.end_date) || firstDay;
    const zone = isValidTimeZone(event?.timezone) ? event.timezone.trim() : 'UTC';
    const at = (day, time) => {
        const instant = Date.parse(naiveInZone(`${day}T${time}`, zone));
        return Number.isFinite(instant) ? instant : null;
    };
    const start = clockOf(event?.start_time);
    const rawEnd = event?.end_time;
    const hasEnd = rawEnd !== null && rawEnd !== undefined && rawEnd !== '';
    const duration = event?.duration_minutes;
    // The same rule as the server's `registration_window` (services/form_event_registration.py), which decides what the
    // form accepts: the picker must never list an event the server calls over.
    if (hasEnd) {
        // A time that does not parse reads as the end of the last day, never as a duration.
        const end = clockOf(rawEnd);
        if (end) {
            // A multi-day event's last day carries its own end time; only a single-day event rolls past midnight.
            const crossesMidnight = lastDay === firstDay && start && end.minutes <= start.minutes;
            const ends = at(crossesMidnight ? addDays(lastDay, 1) : lastDay, end.text);
            if (ends !== null) return ends;
        }
    } else if (start && Number.isInteger(duration) && duration > 0) {
        const began = at(lastDay, start.text);
        if (began !== null) return began + duration * 60000;
    }
    return at(addDays(lastDay, 1), '00:00');
}

/** True when `now` is at or after the end of the event's registration window (E12). An undated event never ends. */
export function registrationEnded(event, now = new Date()) {
    const ends = registrationWindowEnd(event);
    if (ends === null) return false;
    const at = now instanceof Date ? now.getTime() : (typeof now === 'number' ? now : Date.parse(now));
    return Number.isFinite(at) && at >= ends;
}

// ─── The picker's list (E18) ─────────────────────────────────────────────────────────────────────────────────

/**
 * One list out of the picker's two status-filtered reads. `list_events` sorts newest first and stops at 100 rows, so
 * the picker merges both pages, keeps one row per `event_id`, drops events whose registration window has ended and
 * sorts what is left soonest first (by `date`, then `start_time`; an undated event goes last).
 */
export function mergeRegistrationEvents(pages, now = new Date()) {
    const seen = new Map();
    for (const page of pages || []) {
        for (const event of Array.isArray(page) ? page : []) {
            const id = event?.event_id;
            if (typeof id !== 'string' || !id || seen.has(id)) continue;
            if (registrationEnded(event, now)) continue;
            seen.set(id, event);
        }
    }
    const key = (event) => `${dayOf(event.date) || '9999-99-99'} ${clockOf(event.start_time)?.text || '99:99'}`;
    return [...seen.values()].sort((a, b) => key(a).localeCompare(key(b)));
}

// ─── The Settings section's notices (E54) ────────────────────────────────────────────────────────────────────

/**
 * The amber callouts under the "Event registration" section, as [{code, text}] in the order they read.
 *
 * `state` and `status` come from `GET /api/forms/{id}/event-registration` (E19): `state` is the server's open check
 * (`open`, `not_open`, `closed`, `full`) and `status` is the canonical event status, or "deleted". `closed` reads
 * "cancelled" or "deleted" by status and "over" for everything else (completed, or past its window). A callout for
 * the email field appears only when `hasEmailField` is exactly false, so a caller that doesn't know passes nothing.
 */
export function registrationCallouts({ state, status, confirmed, capacity, hasEmailField: hasEmail } = {}) {
    const callouts = [];
    if (state === 'not_open') {
        callouts.push({
            code: 'draft',
            text: "This event is a draft. The form says 'Registration opens soon' until you publish it.",
        });
    } else if (state === 'full') {
        const seats = Number.isFinite(confirmed) && Number.isFinite(capacity) ? ` (${confirmed} of ${capacity})` : '';
        callouts.push({
            code: 'full',
            text: `This event is full${seats}. The form now says 'This event is full'.`,
        });
    } else if (state === 'closed') {
        const why = status === 'deleted' ? 'deleted' : status === 'cancelled' ? 'cancelled' : 'over';
        callouts.push({
            code: 'closed',
            text: `This event is ${why}. The form now says 'Registration is closed'. Choose another event or turn registration off.`,
        });
    }
    if (hasEmail === false) {
        callouts.push({
            code: 'no_email',
            text: 'Add an Email field — a respondent can only be registered with an email address.',
        });
    }
    return callouts;
}

// ─── The email check (E54) ───────────────────────────────────────────────────────────────────────────────────

const isEmailField = (field) => field?.field_type === 'email' || field?.map_to === 'email';

/**
 * True when the form can resolve a respondent's email, over EVERY step and not only the one this builder session
 * edits: the active step's live `fields` are swapped in first (the merge `buildSavePayload` does), and a legacy form
 * with no steps reads its own fields. A field counts when it is an email field, is mapped to `email`, or is the one
 * `settings.email_field_id` names: the server's `resolve_email` rule (`services/lead_capture.py`).
 */
export function hasEmailField({ steps, activeStepIdx = -1, fields, form } = {}) {
    const stepList = Array.isArray(steps) ? steps : [];
    const live = Array.isArray(fields) ? fields : null;
    const editing = activeStepIdx >= 0 && stepList[activeStepIdx] ? activeStepIdx : -1;
    let all;
    if (stepList.length) {
        const merged = editing >= 0 && live
            ? stepList.map((step, i) => (i === editing ? { ...step, fields: live } : step))
            : stepList;
        all = getAllFieldsFromSteps(merged);
        // No step is being edited: the live fields are the top-level copy, which no step carries.
        if (editing < 0 && live) all = all.concat(flattenInputFields(live));
    } else {
        all = flattenInputFields(live || form?.fields || []);
    }
    if (all.some(isEmailField)) return true;
    const emailFieldId = form?.settings?.email_field_id;
    return Boolean(emailFieldId) && all.some((field) => field.field_id === emailFieldId);
}
